import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { ApiError } from '@/shared/api-client';
import { Button } from '@/shared/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/components/card';
import { Input } from '@/shared/ui/components/input';
import { SignaturePad, type SignaturePadHandle } from '@/shared/ui/SignaturePad';
import {
  getPublicWaiverTemplate,
  signPublicWaiver,
  type PublicWaiverSignInput,
  type PublicWaiverSignResponse,
  type PublicWaiverTemplate,
} from './publicWaivers';

type ChildParticipantForm = {
  id: string;
  childName: string;
  dateOfBirth: string;
};

type ChildParticipantField = keyof Omit<ChildParticipantForm, 'id'>;

type ChildParticipantErrors = {
  childName?: string;
  dateOfBirth?: string;
};

type FormState = {
  signerName: string;
  includeSelf: boolean;
  childParticipants: ChildParticipantForm[];
  contact: string;
  acknowledgements: boolean[];
};

type SubmissionDraft = {
  input: PublicWaiverSignInput;
  childIndexByParticipantIndex: Record<number, number>;
};

type ParticipantErrorField = keyof ChildParticipantErrors;

const MAX_PARTICIPANTS = 20;
const PARTICIPANT_LIMIT_MESSAGE = `You can sign for up to ${MAX_PARTICIPANTS} participants in one submission.`;

let nextParticipantId = 0;

function createChildParticipant(): ChildParticipantForm {
  nextParticipantId += 1;
  return {
    id: `participant-${nextParticipantId}`,
    childName: '',
    dateOfBirth: '',
  };
}

function currentParticipantCount(form: FormState): number {
  return form.childParticipants.length + (form.includeSelf ? 1 : 0);
}

function coveredParticipantNames(form: FormState): string[] {
  const names: string[] = [];
  const signerName = form.signerName.trim();

  if (form.includeSelf && signerName) {
    names.push(signerName);
  }

  form.childParticipants.forEach((participant) => {
    const childName = participant.childName.trim();
    if (childName) {
      names.push(childName);
    }
  });

  return names;
}

function isEmail(value: string): boolean {
  return /\S+@\S+\.\S+/.test(value);
}

function contactFields(contact: string): { phone: string | null; email: string | null } {
  return isEmail(contact) ? { phone: null, email: contact } : { phone: contact, email: null };
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Could not submit the waiver. Please try again.';
}

function validateDateOfBirth(input: string): string | null {
  if (!input) return null;
  const date = new Date(`${input}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return 'Enter a valid date of birth.';
  return null;
}

function isParticipantErrorField(value: string): value is ParticipantErrorField {
  return value === 'childName' || value === 'dateOfBirth';
}

function parseParticipantErrors(
  error: ApiError,
  childIndexByParticipantIndex: Record<number, number>,
): ChildParticipantErrors[] {
  const errors: ChildParticipantErrors[] = [];
  const details = error.details ?? {};
  for (const [field, message] of Object.entries(details)) {
    const match = /^participants\[(\d+)\]\.(childName|dateOfBirth)$/.exec(field);
    if (!match || !message) {
      continue;
    }
    const participantIndexText = match[1];
    const participantField = match[2];
    if (!participantIndexText || !participantField || !isParticipantErrorField(participantField)) {
      continue;
    }
    const participantIndex = Number(participantIndexText);
    const childIndex = childIndexByParticipantIndex[participantIndex];
    if (childIndex === undefined) {
      continue;
    }
    errors[childIndex] = {
      ...errors[childIndex],
      [participantField]: message,
    };
  }
  return errors;
}

function assignParticipantError(
  errors: ChildParticipantErrors[],
  childIndex: number,
  field: ChildParticipantField,
  message: string,
) {
  errors[childIndex] = {
    ...errors[childIndex],
    [field]: message,
  };
}

export function PublicWaiverPage() {
  const { venueId = '' } = useParams();
  const signatureRef = useRef<SignaturePadHandle | null>(null);
  const childIndexByParticipantIndexRef = useRef<Record<number, number>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [participantErrors, setParticipantErrors] = useState<ChildParticipantErrors[]>([]);
  const [submittedRecords, setSubmittedRecords] = useState<
    PublicWaiverSignResponse['records'] | null
  >(null);
  const [form, setForm] = useState<FormState>({
    signerName: '',
    includeSelf: false,
    childParticipants: [createChildParticipant()],
    contact: '',
    acknowledgements: [],
  });

  const templateQuery = useQuery({
    queryKey: ['public-waiver-template', venueId],
    queryFn: () => getPublicWaiverTemplate(venueId),
    enabled: venueId.length > 0,
  });

  const submitMutation = useMutation({
    mutationFn: signPublicWaiver,
    onSuccess: (response) => {
      childIndexByParticipantIndexRef.current = {};
      setParticipantErrors([]);
      setSubmitError(null);
      setSubmittedRecords(response.records);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const nextParticipantErrors = parseParticipantErrors(
          error,
          childIndexByParticipantIndexRef.current,
        );
        setParticipantErrors(nextParticipantErrors);
        setSubmitError(nextParticipantErrors.length > 0 ? null : errorMessage(error));
        return;
      }
      setParticipantErrors([]);
      setSubmitError(errorMessage(error));
    },
  });

  if (submittedRecords) {
    const coveredNames = submittedRecords
      .map((record) => record.childName ?? record.signerName)
      .join(', ');
    return (
      <section className="mx-auto grid min-h-screen max-w-3xl place-items-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>
              <h1 className="text-2xl font-semibold">You&apos;re all set</h1>
            </CardTitle>
            <CardDescription>{coveredNames}</CardDescription>
          </CardHeader>
          <CardContent>
            <p>Let the front desk know you&apos;re ready.</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  const template = templateQuery.data;
  const acknowledgements = template?.acknowledgements ?? [];
  const coveredNames = coveredParticipantNames(form);

  function clearSubmissionFeedback() {
    setSubmitError(null);
    setParticipantErrors([]);
  }

  function updateForm<K extends keyof FormState>(key: K, value: FormState[K]) {
    clearSubmissionFeedback();
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateChildParticipant(id: string, key: ChildParticipantField, value: string) {
    clearSubmissionFeedback();
    setForm((current) => ({
      ...current,
      childParticipants: current.childParticipants.map((participant) =>
        participant.id === id ? { ...participant, [key]: value } : participant,
      ),
    }));
  }

  function toggleAcknowledgement(index: number, checked: boolean) {
    clearSubmissionFeedback();
    setForm((current) => {
      const next = [...current.acknowledgements];
      next[index] = checked;
      return { ...current, acknowledgements: next };
    });
  }

  function addChildParticipant() {
    clearSubmissionFeedback();
    if (currentParticipantCount(form) >= MAX_PARTICIPANTS) {
      setSubmitError(PARTICIPANT_LIMIT_MESSAGE);
      return;
    }
    setForm((current) => ({
      ...current,
      childParticipants: [...current.childParticipants, createChildParticipant()],
    }));
  }

  function removeChildParticipant(id: string) {
    clearSubmissionFeedback();
    setForm((current) => {
      const remaining = current.childParticipants.filter((participant) => participant.id !== id);
      const childParticipants =
        remaining.length === 0 && !current.includeSelf ? [createChildParticipant()] : remaining;
      return { ...current, childParticipants };
    });
  }

  function toggleIncludeSelf(checked: boolean) {
    clearSubmissionFeedback();
    setForm((current) => {
      let childParticipants = current.childParticipants;
      if (!checked && childParticipants.length === 0) {
        childParticipants = [createChildParticipant()];
      }
      return {
        ...current,
        includeSelf: checked,
        childParticipants,
      };
    });
  }

  function createSubmission(template: PublicWaiverTemplate): SubmissionDraft | string {
    const signerName = form.signerName.trim();
    const contact = form.contact.trim();
    const nextParticipantErrors: ChildParticipantErrors[] = [];

    if (!signerName) return 'Enter your name.';

    const participants: PublicWaiverSignInput['participants'] = [];
    const childIndexByParticipantIndex: Record<number, number> = {};

    if (form.includeSelf) {
      participants.push({
        signerCapacity: 'SELF',
        childName: null,
        dateOfBirth: null,
      });
    }

    form.childParticipants.forEach((participant, childIndex) => {
      const childName = participant.childName.trim();
      const dateOfBirth = participant.dateOfBirth.trim();
      const dateOfBirthError = validateDateOfBirth(dateOfBirth);

      if (!childName) {
        assignParticipantError(
          nextParticipantErrors,
          childIndex,
          'childName',
          'Enter the child name.',
        );
      }
      if (!dateOfBirth) {
        assignParticipantError(
          nextParticipantErrors,
          childIndex,
          'dateOfBirth',
          'Enter the child date of birth.',
        );
      } else if (dateOfBirthError) {
        assignParticipantError(nextParticipantErrors, childIndex, 'dateOfBirth', dateOfBirthError);
      }

      participants.push({
        signerCapacity: 'GUARDIAN_FOR_CHILD',
        childName: childName || null,
        dateOfBirth: dateOfBirth || null,
      });
      childIndexByParticipantIndex[participants.length - 1] = childIndex;
    });

    if (participants.length === 0) {
      return 'Add at least one participant.';
    }
    if (nextParticipantErrors.some(Boolean)) {
      setParticipantErrors(nextParticipantErrors);
      return 'Fix the highlighted participant details.';
    }
    if (!contact) return 'Enter a phone number or email.';
    if (
      template.acknowledgements.some(
        (acknowledgement, index) => acknowledgement.required && !form.acknowledgements[index],
      )
    ) {
      return 'Accept the required acknowledgements.';
    }

    const signature = signatureRef.current?.read();
    if (!signature || signature.kind === 'blank') return 'Add your signature.';
    if (signature.kind === 'too_large') return 'Signature is too large. Clear it and try again.';

    const { phone, email } = contactFields(contact);
    return {
      input: {
        venueId,
        signerName,
        phone,
        email,
        participants,
        signatureDataUrl: signature.dataUrl,
      },
      childIndexByParticipantIndex,
    };
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template) return;

    const submission = createSubmission(template);
    if (typeof submission === 'string') {
      setSubmitError(submission);
      return;
    }

    childIndexByParticipantIndexRef.current = submission.childIndexByParticipantIndex;
    clearSubmissionFeedback();
    submitMutation.mutate(submission.input);
  }

  return (
    <section className="mx-auto grid min-h-screen max-w-4xl p-6 md:p-10">
      <div className="grid gap-6 self-center">
        <Card>
          <CardHeader>
            <CardTitle>
              <h1 className="text-2xl font-semibold">Sign waiver</h1>
            </CardTitle>
            <CardDescription>Complete this form before checking in at the venue.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            {templateQuery.isLoading && <p>Loading waiver…</p>}
            {templateQuery.isError && (
              <p role="alert" className="text-destructive text-sm">
                {errorMessage(templateQuery.error)}
              </p>
            )}
            {template && (
              <>
                <article
                  className="prose prose-sm max-w-none rounded-lg border bg-muted/30 p-4"
                  dangerouslySetInnerHTML={{ __html: template.bodyHtml }}
                />
                <form className="grid gap-5" onSubmit={submit} noValidate>
                  <label className="grid gap-2" htmlFor="public-waiver-signer-name">
                    <span className="text-sm font-medium">Your name</span>
                    <Input
                      id="public-waiver-signer-name"
                      value={form.signerName}
                      onChange={(event) => updateForm('signerName', event.target.value)}
                    />
                  </label>

                  <div className="flex items-start gap-3 rounded-lg border p-4 text-sm">
                    <input
                      id="public-waiver-include-self"
                      type="checkbox"
                      checked={form.includeSelf}
                      onChange={(event) => toggleIncludeSelf(event.target.checked)}
                    />
                    <label htmlFor="public-waiver-include-self" className="cursor-pointer">
                      <span className="block font-medium">Include yourself</span>
                      <span className="text-muted-foreground">
                        Add yourself to the same signing batch if you are participating too.
                      </span>
                    </label>
                  </div>

                  <div className="grid gap-4">
                    {form.childParticipants.map((participant, index) => {
                      const errors = participantErrors[index];
                      return (
                        <fieldset key={participant.id} className="grid gap-4 rounded-lg border p-4">
                          <legend className="px-1 text-sm font-medium">
                            Child participant {index + 1}
                          </legend>
                          <label
                            className="grid gap-2"
                            htmlFor={`public-waiver-child-name-${participant.id}`}
                          >
                            <span className="text-sm font-medium">Child name</span>
                            <Input
                              id={`public-waiver-child-name-${participant.id}`}
                              value={participant.childName}
                              onChange={(event) =>
                                updateChildParticipant(
                                  participant.id,
                                  'childName',
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                          {errors?.childName && (
                            <p role="alert" className="text-destructive text-sm">
                              {errors.childName}
                            </p>
                          )}

                          <label
                            className="grid gap-2"
                            htmlFor={`public-waiver-date-of-birth-${participant.id}`}
                          >
                            <span className="text-sm font-medium">Child date of birth</span>
                            <Input
                              id={`public-waiver-date-of-birth-${participant.id}`}
                              type="date"
                              value={participant.dateOfBirth}
                              onChange={(event) =>
                                updateChildParticipant(
                                  participant.id,
                                  'dateOfBirth',
                                  event.target.value,
                                )
                              }
                            />
                          </label>
                          {errors?.dateOfBirth && (
                            <p role="alert" className="text-destructive text-sm">
                              {errors.dateOfBirth}
                            </p>
                          )}

                          <div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => removeChildParticipant(participant.id)}
                            >
                              Remove participant
                            </Button>
                          </div>
                        </fieldset>
                      );
                    })}

                    <div>
                      <Button type="button" variant="outline" onClick={addChildParticipant}>
                        Add another child
                      </Button>
                    </div>
                  </div>

                  <fieldset className="grid gap-3 rounded-lg border p-4">
                    <legend className="px-1 text-sm font-medium">People covered</legend>
                    {coveredNames.length > 0 ? (
                      <ul className="grid gap-1 text-sm">
                        {coveredNames.map((name, index) => (
                          <li key={`${index}-${name}`}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        Add participants to review who this waiver will cover.
                      </p>
                    )}
                  </fieldset>

                  <label className="grid gap-2" htmlFor="public-waiver-contact">
                    <span className="text-sm font-medium">Phone or email</span>
                    <Input
                      id="public-waiver-contact"
                      value={form.contact}
                      onChange={(event) => updateForm('contact', event.target.value)}
                    />
                  </label>

                  {acknowledgements.length > 0 && (
                    <fieldset className="grid gap-3">
                      <legend className="text-sm font-medium">Acknowledgements</legend>
                      {acknowledgements.map((ack, index) => (
                        <label
                          key={`${ack.text}-${index}`}
                          className="flex items-start gap-3 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={!!form.acknowledgements[index]}
                            onChange={(event) => toggleAcknowledgement(index, event.target.checked)}
                          />
                          <span>
                            {ack.text}
                            {ack.required ? ' *' : ''}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                  )}

                  <div className="grid gap-2">
                    <span className="text-sm font-medium">Signature</span>
                    <SignaturePad ref={signatureRef} ariaLabel="Signature" />
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          signatureRef.current?.clear();
                          clearSubmissionFeedback();
                        }}
                      >
                        Clear signature
                      </Button>
                    </div>
                  </div>

                  {submitError && (
                    <p role="alert" className="text-destructive text-sm">
                      {submitError}
                    </p>
                  )}

                  <Button type="submit" disabled={submitMutation.isPending}>
                    {submitMutation.isPending ? 'Submitting…' : 'Submit waiver'}
                  </Button>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
